import React, { useEffect, useMemo, useState } from 'react';
import { Button, Combobox, Field, inputClass, useAdminApi, useToast } from './ui';

const emptyForm = { directory: '', repo: '', placeId: '', name: '', action: 'suggest', frequency: 'daily' };

function AddIntegrationModal({ data, reload, onClose, onCreated }) {
  const api = useAdminApi();
  const toast = useToast();

  const [mode, setMode] = useState('file'); // file = scraper already on the server, git = clone a repo
  const [scrapers, setScrapers] = useState(null);
  const [showLinked, setShowLinked] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api('/admin/scrapers')
      .then((result) => setScrapers(result.scrapers))
      .catch((error) => { setScrapers([]); toast(error.message, 'error'); });
  }, [api, toast]);

  const regionById = useMemo(() => Object.fromEntries(data.regions.map((r) => [r.id, r])), [data.regions]);
  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }));

  const fileOptions = (scrapers || []).filter((s) => showLinked || !s.linked);
  const selectedScraper = (scrapers || []).find((s) => `${s.region}/${s.directory}` === form.directory);

  // For an existing file, only offer places in that file's region
  const placeOptions = useMemo(() => {
    const regionFilter = mode === 'file' && selectedScraper ? selectedScraper.region : null;
    return data.places
      .filter((place) => !regionFilter || (regionById[place.regionId] && regionById[place.regionId].urlName === regionFilter))
      .map((place) => ({ value: place.id, label: place.name, sub: regionById[place.regionId] ? regionById[place.regionId].name : '' }));
  }, [data.places, regionById, mode, selectedScraper]);

  const place = data.places.find((p) => String(p.id) === String(form.placeId));
  const directory = mode === 'file' ? (selectedScraper ? selectedScraper.directory : '') : form.directory.trim();
  const canCreate = !!place && !!directory && (mode === 'file' || !!form.repo.trim());

  const create = async () => {
    setSaving(true);
    try {
      const body = {
        placeId: place.id,
        type: 'Scraper',
        directory,
        name: form.name.trim() || `${place.name} Scraper`,
        action: form.action,
        frequency: form.frequency,
        ...(mode === 'git' && { repo: form.repo.trim() }),
      };
      const created = await api('/admin/integration', { method: 'POST', body });
      const integration = created.placeIntegration;

      if (mode === 'git') {
        try {
          await api(`/admin/integration/${integration.uuid}/import-code`, { method: 'POST' });
          toast('Integration created and code imported.');
        } catch (error) {
          toast(`Integration created, but the git import failed: ${error.message}`, 'error');
        }
      } else {
        toast('Integration created. It is “new”, so test it, then approve it.');
      }

      await reload({ silent: true });
      onCreated(integration);
    } catch (error) {
      toast(error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center bg-black/40 p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 mt-16" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-semibold">Add scraper integration</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-black" aria-label="Close">✕</button>
        </div>

        <div className="flex gap-2 mb-4">
          {[['file', 'Scraper already on the server'], ['git', 'Clone from git']].map(([value, label]) => (
            <button
              key={value}
              onClick={() => { setMode(value); setForm(emptyForm); }}
              className={`px-3 py-1.5 text-sm rounded-md border ${mode === value ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-300 hover:bg-gray-50'}`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="space-y-4">
          {mode === 'file' ? (
            <Field label="Scraper file" hint="Files in cronjobs/scrapers that no integration uses yet are listed first.">
              <select className={inputClass} value={form.directory} onChange={(e) => setForm({ ...form, directory: e.target.value, placeId: '' })}>
                <option value="">{scrapers === null ? 'Loading…' : 'Choose a scraper…'}</option>
                {fileOptions.map((s) => (
                  <option key={`${s.region}/${s.directory}`} value={`${s.region}/${s.directory}`}>
                    {s.region} / {s.directory}{s.linked ? ' (already linked)' : ''}
                  </option>
                ))}
              </select>
              <label className="flex items-center gap-2 text-xs text-gray-600 mt-2">
                <input type="checkbox" checked={showLinked} onChange={(e) => setShowLinked(e.target.checked)} /> Also show files that already have an integration
              </label>
            </Field>
          ) : (
            <>
              <Field label="Git repository" hint="https:// or git@ URL. It is cloned into cronjobs/scrapers/<region>/<directory>.">
                <input className={inputClass} value={form.repo} onChange={(e) => set('repo')(e.target.value)} placeholder="https://github.com/…" />
              </Field>
              <Field label="Directory name" hint="Letters, numbers, . _ - only. Must not exist yet.">
                <input className={inputClass} value={form.directory} onChange={(e) => set('directory')(e.target.value)} placeholder="e.g. new-venue-scraper" />
              </Field>
            </>
          )}

          <Field label="Place">
            <Combobox options={placeOptions} value={form.placeId} onChange={set('placeId')} placeholder="Search places…" />
          </Field>

          <Field label="Label" hint="Optional. Defaults to “<place> Scraper”.">
            <input className={inputClass} value={form.name} onChange={(e) => set('name')(e.target.value)} />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Access level">
              <select className={inputClass} value={form.action} onChange={(e) => set('action')(e.target.value)}>
                <option value="suggest">Suggest</option>
                <option value="add">Full (add/edit)</option>
                <option value="disabled">Disabled</option>
              </select>
            </Field>
            <Field label="Frequency">
              <select className={inputClass} value={form.frequency} onChange={(e) => set('frequency')(e.target.value)}>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="">Manual only</option>
              </select>
            </Field>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={saving} disabled={!canCreate} onClick={create}>Create integration</Button>
        </div>
      </div>
    </div>
  );
}

export default AddIntegrationModal;
